import React from 'react';
import {Box, Stack} from '@mui/material';
import {Body, Headline, Label} from '@lightricks/react-design-system';
import {formattedDate} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import {hasActionsForState} from '@/config/campaignAction';
import PERMISSIONS from '@/config/permissions';
import {SHORT_DAY_NUM_SHORT_MONTH_YEAR_FORMAT} from '@/config/timeFormats';
import Link from '@/components/link';
import {CampaignListItem} from '@/hooks/queries/use-campaigns-v2-list-query/useCampaignsV2ListQuery';
import useDashboardUserPermissions from '@/hooks/use-dashboard-user-permissions/useDashboardUserPermissions';
import {getCampaignCardUrl} from '../../utils/getCampaignCardUrl';
import CampaignActionsMenu from '../campaign-actions-menu';
import CampaignNetworkLabel from '../campaign-network-label';
import CampaignStatBoxes from '../campaign-stat-boxes/CampaignStatBoxes';
import CampaignStatusChip from '../campaign-status-chip';
import styles from './CampaignCard.module.scss';

interface CampaignCardProps {
  campaign: CampaignListItem;
}

export default function CampaignCard({campaign}: CampaignCardProps) {
  const {
    cloneCampaign: canCloneCampaign,
    revertToDraftCampaign: canRevertCampaign,
    deleteCampaign: canDeleteCampaign,
    editCampaign: canEditCampaign,
    closeProposalQueueCampaign: canPauseApplications,
    reopenProposalQueueCampaign: canResumeApplications,
  } = useDashboardUserPermissions([
    PERMISSIONS.CAMPAIGN.CLONE,
    PERMISSIONS.CAMPAIGN.REVERT_TO_DRAFT,
    PERMISSIONS.CAMPAIGN.DELETE,
    PERMISSIONS.CAMPAIGN.EDIT,
    PERMISSIONS.CAMPAIGN.CLOSE_PROPOSAL_QUEUE,
    PERMISSIONS.CAMPAIGN.REOPEN_PROPOSAL_QUEUE,
  ]);

  const campaignUrl = getCampaignCardUrl(campaign, canEditCampaign);

  const showMoreButton = hasActionsForState(campaign.state, {
    generatedByBriefAi: campaign.generatedByBriefAi,
    network: campaign.network,
    canClone: canCloneCampaign,
    canRevert: canRevertCampaign,
    canDelete: canDeleteCampaign,
    canEdit: canEditCampaign,
    canPauseApplications,
    canResumeApplications,
  });

  const launchedOn = campaign.launchedOn
    ? new Date(campaign.launchedOn)
    : undefined;

  // When there's no launch date, render nothing in the date slot (no em-dash).
  const formattedLaunchDate = launchedOn
    ? formattedDate(launchedOn, SHORT_DAY_NUM_SHORT_MONTH_YEAR_FORMAT)
    : null;

  return (
    <Link
      to={campaignUrl}
      className={styles.card}
      data-testid={`campaign-card-${campaign.id}`}
    >
      <Box className={styles.thumbnailSide}>
        {campaign.imageUrl ? (
          <img
            src={campaign.imageUrl}
            alt={campaign.name}
            className={styles.thumbnail}
          />
        ) : (
          <Box className={styles.placeholderThumbnail}>
            <Label size="sm">{translate('No image')}</Label>
          </Box>
        )}
      </Box>

      <Stack className={styles.contentSide}>
        <Stack className={styles.headerRow}>
          <Stack className={styles.headerText}>
            <Headline size="md" className={styles.title}>
              {campaign.name}
            </Headline>
            <CampaignNetworkLabel network={campaign.network} />
          </Stack>

          <Stack className={styles.headerActions}>
            {formattedLaunchDate && (
              <Body size="md" className={styles.date}>
                {formattedLaunchDate}
              </Body>
            )}
            <CampaignStatusChip state={campaign.state} />
            {showMoreButton ? (
              <CampaignActionsMenu
                campaignId={campaign.id}
                campaignState={campaign.state}
                generatedByBriefAi={campaign.generatedByBriefAi}
                campaignNetwork={campaign.network}
                canCloneCampaign={canCloneCampaign}
                canRevertCampaign={canRevertCampaign}
                canDeleteCampaign={canDeleteCampaign}
                canEditCampaign={canEditCampaign}
                canPauseApplications={canPauseApplications}
                canResumeApplications={canResumeApplications}
              />
            ) : null}
          </Stack>
        </Stack>

        <CampaignStatBoxes campaign={campaign} />
      </Stack>
    </Link>
  );
}
